// Radha — Privacy console (consent ledger + data-subject rights).
//
// Surfaces the DPDP / GDPR / CCPA machinery on the direct path (migration 032):
// the append-only consent ledger, and the DSAR request lifecycle with an
// in-record processing history. Panel only; the shell owns the chrome. Writes
// (record consent, raise a request, advance a request) are gated to non-auditor
// roles by the shell's RoleContext.
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";
import { useRole } from "../lib/authSupabase";

const KINDS = ["access", "erasure", "portability", "rectification", "restriction", "objection", "opt_out_sale"];
const REGIMES = ["DPDP", "GDPR", "CCPA"];
const STATES = ["received", "verifying", "in_progress", "completed", "rejected"];

const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 500 };
const td: React.CSSProperties = { padding: "9px 8px" };
const input: React.CSSProperties = { padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13 };

interface Consent { id: string; subject_id: string; purpose: string; granted: boolean; basis: string; source: string; created_at: string; }
interface Dsar { id: string; subject_id: string; kind: string; regime: string; state: string; received_at: string; history: unknown[]; }

function useConsent() {
  return useQuery({
    queryKey: ["privacy", "consent"],
    queryFn: async (): Promise<Consent[]> => {
      const { data, error } = await supabase.from("consent_events")
        .select("id, subject_id, purpose, granted, basis, source, created_at")
        .order("created_at", { ascending: false }).limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as Consent[];
    },
  });
}
function useDsar() {
  return useQuery({
    queryKey: ["privacy", "dsar"],
    queryFn: async (): Promise<Dsar[]> => {
      const { data, error } = await supabase.from("dsar_requests")
        .select("id, subject_id, kind, regime, state, received_at, history")
        .order("created_at", { ascending: false }).limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as Dsar[];
    },
  });
}

export function PrivacyPanel() {
  const consent = useConsent();
  const dsar = useDsar();
  const { canWrite } = useRole();
  const qc = useQueryClient();

  const [subject, setSubject] = useState("");
  const [kind, setKind] = useState("access");
  const [regime, setRegime] = useState("DPDP");

  const create = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("dsar_requests")
        .insert({ subject_id: subject.trim(), kind, regime, state: "received" });
      if (error) throw error;
    },
    onSuccess: () => { setSubject(""); qc.invalidateQueries({ queryKey: ["privacy", "dsar"] }); },
  });

  const advance = useMutation({
    mutationFn: async ({ id, state }: { id: string; state: string }) => {
      const { error } = await supabase.from("dsar_requests").update({ state }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["privacy", "dsar"] }),
  });

  return (
    <div>
      <div style={{ display: "flex", gap: 24, margin: "4px 0 22px", flexWrap: "wrap" }}>
        <Stat label="Consent records" value={String((consent.data ?? []).length)} />
        <Stat label="Open requests" value={String((dsar.data ?? []).filter((d) => d.state !== "completed" && d.state !== "rejected").length)} />
        <Stat label="Regimes" value="DPDP · GDPR · CCPA" />
      </div>

      <h3 style={{ font: "600 15px Inter", margin: "0 0 8px" }}>Data-subject requests</h3>
      {canWrite && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
          <input style={{ ...input, minWidth: 180 }} placeholder="Subject (e.g. debtor:acme)" value={subject} onChange={(e) => setSubject(e.target.value)} />
          <select style={input} value={kind} onChange={(e) => setKind(e.target.value)}>{KINDS.map((k) => <option key={k}>{k}</option>)}</select>
          <select style={input} value={regime} onChange={(e) => setRegime(e.target.value)}>{REGIMES.map((r) => <option key={r}>{r}</option>)}</select>
          <button
            style={{ ...input, background: "var(--accent-purple)", color: "#fff", border: 0, cursor: "pointer" }}
            disabled={!subject.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Raising…" : "Raise request"}
          </button>
        </div>
      )}

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 12 }}>
              <th style={th}>Subject</th><th style={th}>Kind</th><th style={th}>Regime</th>
              <th style={th}>State</th><th style={th}>Steps</th>
            </tr>
          </thead>
          <tbody>
            {(dsar.data ?? []).map((d) => (
              <tr key={d.id} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={td}>{d.subject_id}</td>
                <td style={{ ...td, textTransform: "capitalize" }}>{d.kind.replace(/_/g, " ")}</td>
                <td style={{ ...td, textTransform: "uppercase" }}>{d.regime}</td>
                <td style={td}>
                  {canWrite ? (
                    <select style={{ ...input, padding: "3px 6px" }} value={d.state}
                      onChange={(e) => advance.mutate({ id: d.id, state: e.target.value })}>
                      {STATES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  ) : (
                    <span style={{ color: d.state === "completed" ? "var(--status-good)" : d.state === "rejected" ? "var(--status-critical)" : "var(--fg)" }}>{d.state}</span>
                  )}
                </td>
                <td style={{ ...td, color: "var(--fg-muted)" }}>{(d.history ?? []).length}</td>
              </tr>
            ))}
            {(dsar.data ?? []).length === 0 && (
              <tr><td style={{ ...td, color: "var(--fg-muted)" }} colSpan={5}>No requests.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <h3 style={{ font: "600 15px Inter", margin: "28px 0 8px" }}>Consent ledger</h3>
      <p style={{ color: "var(--fg-muted)", fontSize: 12, margin: "0 0 8px" }}>Append-only — records are never edited or deleted.</p>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 12 }}>
              <th style={th}>Subject</th><th style={th}>Purpose</th><th style={th}>Consent</th>
              <th style={th}>Lawful basis</th><th style={th}>Source</th>
            </tr>
          </thead>
          <tbody>
            {(consent.data ?? []).map((c) => (
              <tr key={c.id} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={td}>{c.subject_id}</td>
                <td style={{ ...td, textTransform: "capitalize" }}>{c.purpose.replace(/_/g, " ")}</td>
                <td style={{ ...td, color: c.granted ? "var(--status-good)" : "var(--status-critical)" }}>{c.granted ? "granted" : "withdrawn"}</td>
                <td style={{ ...td, textTransform: "capitalize" }}>{c.basis.replace(/_/g, " ")}</td>
                <td style={{ ...td, color: "var(--fg-muted)", textTransform: "capitalize" }}>{c.source.replace(/_/g, " ")}</td>
              </tr>
            ))}
            {(consent.data ?? []).length === 0 && (
              <tr><td style={{ ...td, color: "var(--fg-muted)" }} colSpan={5}>No consent records.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>{label}</div>
      <div style={{ font: "600 20px Inter" }}>{value}</div>
    </div>
  );
}
